/**
 * Created by Jie Gao on 2019-05-20.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {Power3, TimelineLite, TweenLite} from "gsap";
import {DragonTribeGameConfig} from "../../DragonTribeGameConfig";
import {DragonTribeGameModuleConfig} from "../../DragonTribeGameModuleConfig";
import {DragonTribeSoundConfig} from "../../DragonTribeSoundConfig";
import {DTReelController} from "../../reel/DTReelController";
import {DTReelSymbol} from "../../symbols/DTReelSymbol";
import {DTReelSymbolH} from "../../symbols/DTReelSymbolH";

export class WildSymbolNudgeAnimation extends BaseView {
    private _layer:PIXI.Container;
    private _fullWildText:TimelineSprite[];
    private _wildFires:TimelineSprite[];

    private _multipliers:PIXI.Sprite[];
    private _dropAnimations:TimelineLite[] = [];
    private _nudgeBounce:TweenLite;

    constructor() {
        super();
        this._layer = StageManager.getLayer(DragonTribeGameConfig.instance.LAYERS.NUDGE.name);
    }

    protected onResize(resizeData:IResizeData):void {
        if(this._nudgeBounce && this._nudgeBounce.isActive()) {
            this._nudgeBounce.progress(1);
            if(StageManager.legacyScaling) {
                StageManager.stage.position.set(NolimitApplication.screenBounds.left, NolimitApplication.screenBounds.top);
            }
        }
    }

    public resetMultipliers():void {
        this._layer.removeChild(this);
        this._multipliers.forEach((multiplier:PIXI.Sprite) => {
            multiplier.visible = false;
        });
        this._dropAnimations.forEach((animation:TimelineLite) => {
            animation.pause();
            animation.kill();
        });
        this._dropAnimations = [];
    }

    public resetAnimations():void {
        this.stopLoopAnimation(this._wildFires);
        this.stopLoopAnimation(this._fullWildText);
    }

    private stopLoopAnimation(animations:TimelineSprite[]):void {
        animations.forEach((animation:TimelineSprite) => {
            animation.hide();
        });
    }

    public nudgeWild(tl:TimelineLite, reelId:number, symId:number, multiplierOnReel:number, reelSize:number, fastAnimation:boolean):void {
        this._layer.addChild(this);
        Reels.moveSymbolToTop(reelId, symId);
        if(symId < 5) {
            tl.add(this.dropWild(reelId, symId, reelSize, multiplierOnReel, fastAnimation));
        }
        for(let i:number = 0; i < (6 - multiplierOnReel); i++) {
            tl.add(() => {
                (<DTReelSymbol>Reels.getSymbol(reelId, i)!).visible = false;
            });
        }
        tl.add((<DTReelSymbolH>Reels.getSymbol(reelId, symId)!).playEggHit());
        tl.add([
            () => {SlotGame.sound.playEffect(DragonTribeSoundConfig.WILD_EXPAND);},
            this._wildFires[reelId].getAnimationAutoShowHide(true, true),
            this._fullWildText[reelId].getAnimationAutoShowHide(true, false)
        ], "-=0.5");
        tl.add(() => {
            this._fullWildText[reelId].hide();
            this.showStackWild(reelId, multiplierOnReel, reelSize);
        }, `+=${(multiplierOnReel > 1) ? 0.3 : -0.5}`);
    }

    private dropWild(reelId:number, symId:number, reelSize:number, multiplierOnReel:number, fastAnimation:boolean):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const multiplier:PIXI.Sprite = this._multipliers[reelId];
        const topSymId:number = 6 - reelSize;
        let multiplierNumber:number = 1;
        let multiplierName:string;
        const step:number = DTReelController.SYM_SIZE[reelSize].height;
        const reelController:DTReelController = <DTReelController>Reels.getReel(reelId);
        const targetPositions:number[] = reelController.targetPositions;
        if(fastAnimation) {
            const fastNudgeAnimation:TimelineLite = new TimelineLite();
            //drop all symbols
            for(let j:number = (6 - reelSize); j <= 5; j++) {
                const symbol:StateReelSymbol = Reels.getSymbol(reelId, j)!;
                reelController.moveSymbolToTop(j);
                fastNudgeAnimation.add([
                    TweenLite.to(symbol, 0.1, {
                        y : targetPositions[j] + step * (multiplierOnReel + 1)
                    })
                ], 0.03);
            }
            fastNudgeAnimation.add(() => {
                this._nudgeBounce = TweenLite.to(StageManager.stage, 0.1, {y : "+=" + 30, ease : "shakeBounce"});
                multiplierName = `multiX${multiplierOnReel}`;
                multiplierNumber = multiplierOnReel;
                multiplier.visible = true;
                multiplier.texture = GameResources.getTextures(multiplierName)[0];
            });
            tl.add(fastNudgeAnimation);
            this._dropAnimations.push(fastNudgeAnimation);
            return tl;
        }
        for(let i:number = 1; i < multiplierOnReel; i++) {
            //Add multiplier
            //drop multiplier
            const symbolDropAnimation:TimelineLite = new TimelineLite();
            symbolDropAnimation.add(() => {SlotGame.sound.playEffect(`wildNudge${i}`);}, 0.15);
            symbolDropAnimation.add(TweenLite.to(multiplier.scale, 0.1, {x : 0.8, y : 0.8}), 0.1);
            symbolDropAnimation.add(TweenLite.to(multiplier.scale, 0.1, {x : 1.2, y : 1.2}), 0.2);
            symbolDropAnimation.add(TweenLite.to(multiplier.scale, 0.1, {x : 1, y : 1}), 0.3);
            symbolDropAnimation.add(() => {
                multiplierName = `multiX${i + 1}`;
                multiplierNumber = i + 1;
                multiplier.visible = (i == 0 ? false : true);
                multiplier.texture = GameResources.getTextures(multiplierName)[0];
            }, 0.23);
            if((symId + i) < 6) {
                //explode symbol under wild
                const symbolBelow:StateReelSymbol = Reels.getSymbol(reelId, symId + i)!;
                if(Reels.getSymbol(reelId, symId) === null) {
                    debugger;
                    throw new Error(`Error: dropWild explode symbol beblow the wild: Cannot find symbol at reel ${reelId} symbol ${symId}`);
                }
                symbolDropAnimation.add(symbolBelow.changeState({
                    state : DragonTribeGameConfig.instance.SYMBOL_STATES.crack
                }), 0.03);
                symbolDropAnimation.add(DragonTribeGameModuleConfig.symbolWinAnimation.playStoneExplosionAnimation(reelId, symId + i), 0.13);
                symbolDropAnimation.add(TweenLite.to(symbolBelow, 0.2, {alpha : 0}), 0.23);
            }
            //drop all symbols
            for(let j:number = topSymId; j <= symId; j++) {
                const symbol:StateReelSymbol = Reels.getSymbol(reelId, j)!;
                reelController.moveSymbolToTop(j);
                symbolDropAnimation.add([
                    TweenLite.to(symbol, 0.03, {
                        y : targetPositions[j] + step * (i - 1) + 10
                    })
                ], 0.1);
                symbolDropAnimation.add([
                    TweenLite.to(symbol, 0.23, {
                        y : targetPositions[j] + step * i,
                        ease : Power3.easeIn,
                        onComplete : () => {
                            this._nudgeBounce = TweenLite.to(StageManager.stage, 0.1, {
                                y : "+=" + 30,
                                ease : "shakeBounce"
                            });
                        }
                    })
                ], 0.13);
            }
            tl.add(symbolDropAnimation);
            this._dropAnimations.push(symbolDropAnimation);
        }
        return tl;
    }

    public showStackWild(reelId:number, multiplier:number, reelSize:number):void {
        const reelController:DTReelController = <DTReelController>Reels.getReel(reelId);
        reelController.showStackWild(reelSize);
        (<DTReelSymbolH>Reels.getSymbol(reelId, 5)).patchMultiplier(`multiX${multiplier}`);
        this._multipliers[reelId].visible = false;
    }

    protected initAnimations():void {
        this._multipliers = ArrayHelper.initArrayWithValues(DragonTribeGameConfig.instance.REELS_NUM, (i:number) => {
            const multiplier:PIXI.Sprite = new PIXI.Sprite();
            multiplier.x = Helper.getSymbolPositions(i, 0)[0];
            multiplier.visible = false;
            multiplier.y = 136;
            this.addChild(multiplier);
            multiplier.anchor.set(0.5, 0.5);
            return multiplier;
        });
        this._wildFires = ArrayHelper.initArrayWithValues(DragonTribeGameConfig.instance.REELS_NUM, (i:number) => {
            return this.createAnimation("fullWildFire", i, -70);
        });
        this._fullWildText = ArrayHelper.initArrayWithValues(DragonTribeGameConfig.instance.REELS_NUM, (i:number) => {
            return this.createAnimation("fullWildText", i, -20);
        });
        this._wildFires.forEach((animation:TimelineSprite) => {
            animation.blendMode = PIXI.BLEND_MODES.SCREEN;
        });
    }

    private createAnimation(textureName:string, reelId:number, offset:number):TimelineSprite {
        const animation:TimelineSprite = new TimelineSprite(GameResources.getTextures(textureName));
        animation.x = Helper.getSymbolPositions(reelId, 0)[0];
        animation.hide();
        animation.getAnimation([15, 15]);
        animation.y = GameConfig.instance.REEL_AREA_POS_Y + DragonTribeGameConfig.instance.REEL_AREA_TOP + offset;
        this.addChild(animation);
        animation.anchor.set(0.5, 0);
        return animation;
    }
}

